import { Routes, Route, Navigate } from 'react-router-dom';
import { LoginPage } from './routes/LoginPage';
import { DashboardLayout } from './components/DashboardLayout';
import { DashboardPage } from './routes/DashboardPage';
import { EmployeesPage } from './routes/EmployeesPage';
import { EmployeeDetailPage } from './routes/EmployeeDetailPage';
import { EmployeeFormPage } from './routes/EmployeeFormPage';
import { OrgChartPage } from './routes/OrgChartPage';
import { LeavePage } from './routes/LeavePage';
import { ManagerInboxPage } from './routes/ManagerInboxPage';
import { CalendarPage } from './routes/CalendarPage';
import { ConfigPage } from './routes/ConfigPage';
import { AuditPage } from './routes/AuditPage';
import { NotificationsPage } from './routes/NotificationsPage';
import { MyProfilePage } from './routes/MyProfilePage';
import { ChangePasswordPage } from './routes/ChangePasswordPage';
import { AnnouncementsPage } from './routes/AnnouncementsPage';
import { AnnouncementDetailPage } from './routes/AnnouncementDetailPage';
import { AnnouncementFormPage } from './routes/AnnouncementFormPage';
import { HelpPage } from './routes/HelpPage';
import { MundialPage } from './routes/MundialPage';
import { MundialAdminPage } from './routes/MundialAdminPage';
import { BroadcastPage } from './routes/BroadcastPage';
import { CollaboratorHome } from './routes/CollaboratorHome';
import { useAuthStore, useEffectiveRole } from './store/auth.store';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => !!s.token);
  const mustChange = useAuthStore((s) => !!s.user?.mustChangePassword);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustChange) return <Navigate to="/cambiar-password" replace />;
  return <>{children}</>;
}

function RoleGuard({
  allowed,
  children,
}: {
  allowed: Array<'ADMIN' | 'HR' | 'MANAGER' | 'EMPLOYEE'>;
  children: React.ReactNode;
}) {
  const role = useEffectiveRole();
  if (!role || !allowed.includes(role)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Landing: colaboradores ven su dashboard personal; ADMIN/HR/MANAGER el panel. */
function HomeRouter() {
  const role = useEffectiveRole();
  return role === 'EMPLOYEE' ? <CollaboratorHome /> : <DashboardPage />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/cambiar-password" element={<ChangePasswordPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomeRouter />} />
        <Route
          path="colaboradores"
          element={
            <RoleGuard allowed={['ADMIN', 'HR', 'MANAGER']}>
              <EmployeesPage />
            </RoleGuard>
          }
        />
        <Route
          path="colaboradores/nuevo"
          element={
            <RoleGuard allowed={['ADMIN', 'HR']}>
              <EmployeeFormPage />
            </RoleGuard>
          }
        />
        <Route
          path="colaboradores/difusiones"
          element={
            <RoleGuard allowed={['ADMIN', 'HR']}>
              <BroadcastPage />
            </RoleGuard>
          }
        />
        <Route
          path="colaboradores/:id"
          element={
            <RoleGuard allowed={['ADMIN', 'HR', 'MANAGER']}>
              <EmployeeDetailPage />
            </RoleGuard>
          }
        />
        <Route
          path="colaboradores/:id/editar"
          element={
            <RoleGuard allowed={['ADMIN', 'HR']}>
              <EmployeeFormPage />
            </RoleGuard>
          }
        />
        <Route path="organigrama" element={<OrgChartPage />} />
        <Route path="permisos" element={<LeavePage />} />
        <Route
          path="bandeja"
          element={
            <RoleGuard allowed={['ADMIN', 'HR', 'MANAGER']}>
              <ManagerInboxPage />
            </RoleGuard>
          }
        />
        <Route path="calendario" element={<CalendarPage />} />
        <Route path="mundial" element={<MundialPage />} />
        <Route
          path="mundial/admin"
          element={
            <RoleGuard allowed={['ADMIN']}>
              <MundialAdminPage />
            </RoleGuard>
          }
        />
        <Route path="anuncios" element={<AnnouncementsPage />} />
        <Route path="anuncios/nuevo" element={<RoleGuard allowed={['ADMIN', 'HR']}><AnnouncementFormPage /></RoleGuard>} />
        <Route path="anuncios/:id" element={<AnnouncementDetailPage />} />
        <Route path="anuncios/:id/editar" element={<RoleGuard allowed={['ADMIN', 'HR']}><AnnouncementFormPage /></RoleGuard>} />
        <Route path="notificaciones" element={<NotificationsPage />} />
        <Route path="ayuda" element={<HelpPage />} />
        <Route path="mi-perfil" element={<MyProfilePage />} />
        <Route
          path="configuracion"
          element={
            <RoleGuard allowed={['ADMIN', 'HR']}>
              <ConfigPage />
            </RoleGuard>
          }
        />
        <Route
          path="auditoria"
          element={
            <RoleGuard allowed={['ADMIN']}>
              <AuditPage />
            </RoleGuard>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
